import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { ActionMenu, TableColumnType } from '@ui';
import { makeCellTextRenderers } from '@features/components/TableCellTextMR';
import { Hospital, HospitalListFilters } from '@/api/apis/facilities/hospital';
import { getShortTzByIanaName } from '@/utils/helpers';
import { buildEditDeleteActionsMenu } from '../../components/EditDeleteActionsMenu';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const HOSPITALS_LIST_FILTERS_BASE: HospitalListFilters = {
  countries: [],
  states: [],
};

export const FILTERS_NAMES: Record<string, string> = {
  countries: 'Country',
  states: 'State',
};

export const HOSPITAL_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'Donor Hospital Name', { sortable: true, minWidthClassName: 'min-w-80' }),
  COMMON_COLUMN_PRESETS.code,
  COMMON_COLUMN_PRESETS.timezone,
  COMMON_COLUMN_PRESETS.address,
  COMMON_COLUMN_PRESETS.city,
  COMMON_COLUMN_PRESETS.state,
  COMMON_COLUMN_PRESETS.postalCode,
  COMMON_COLUMN_PRESETS.actions,
];

export const getRenderHospitalTableCell =
  (
    onEdit: (hospitalId: string) => void,
    onDelete: (hospitalId: string) => void,
    resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
  ) =>
  (hospital: Hospital, columnKey: Key): ReactNode => {
    const { id, logoPath, name, code, location } = hospital;
    const { text, textU } = makeCellTextRenderers();

    const actions = buildEditDeleteActionsMenu({
      editConfig: { key: 'edit-hospital', to: 'edit-hospital', onAction: () => onEdit(id) },
      deleteConfig: { key: 'delete-hospital', to: 'delete-hospital', onAction: () => onDelete(id) },
    });

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/hospitals/locations/${id}`} className="flex items-center gap-2">
            <TmdxAvatar
              radius="xl"
              size="xl"
              name={code || resolveSubdivisionName(location.state, location.country)}
              type="facility"
              src={logoPath}
              fallbackInitials={false}
            />
            <span className="text-m-m">{name}</span>
          </Link>
        );
      case 'code':
        return textU(code || '-');
      case 'timezone':
        return textU(getShortTzByIanaName(location?.timezone) || '-');
      case 'address':
        return text(location?.addressLine || '-');
      case 'city':
        return text(location?.city || '-');
      case 'state':
        return textU(resolveSubdivisionName(location?.state, location?.country) || '-');
      case 'postalCode':
        return text(location?.postalCode || '-');
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={actions}
            MenuTrigger={<MdOutlineMoreHoriz size={20} />}
            label={`${name} actions menu`}
          />
        );
      default:
        return null;
    }
  };
